import type { RenderComponentProps } from "replo-runtime/shared/types";
import type { Component } from "schemas/component";

import React, { useState } from "react";

import unescape from "lodash-es/unescape";
import { ChevronDown } from "lucide-react";

import { ReploComponent } from "../../ReploComponent";
import { useBundleBuilder } from "../BundleBuilderContext";
import { BundlePreview } from "./BundlePreview";

const getCustomProps = (props: Component["props"]) => {
  const { subtotalText, savingsText } = props;
  return {
    subtotalText: subtotalText as string | null,
    savingsText: savingsText as string | null,
  };
};

export const BundleFooter: React.FC<RenderComponentProps> = ({
  componentAttributes,
  component,
  context,
}) => {
  const { subtotalText, savingsText } = getCustomProps(component.props);
  const { getBundleTotals, getBundleTotalQuantity } = useBundleBuilder();
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const { total, savings } = getBundleTotals();
  const totalQuantity = getBundleTotalQuantity();

  const addToCartButtonComponent = component.children?.find(
    ({ type }) => type === "bundleAddToCartButton",
  );

  return (
    <div
      {...componentAttributes}
      style={{
        boxShadow: "0 -2px 10px rgba(0,0,0,0.1)",
        zIndex: 100,
        ...componentAttributes?.style,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{ fontWeight: "600" }}>
          {subtotalText ?? "Subtotal:"}{" "}
          <span dangerouslySetInnerHTML={{ __html: unescape(total) }} />
        </div>
        {totalQuantity > 0 && (
          <div
            style={{
              borderRadius: "4px",
              padding: "6px",
              color: "#059669",
              backgroundColor: "#ecfdf5",
            }}
          >
            {savingsText ?? "You're saving"}{" "}
            <span dangerouslySetInnerHTML={{ __html: unescape(savings) }} />
          </div>
        )}
      </div>
      {addToCartButtonComponent && (
        <ReploComponent
          component={addToCartButtonComponent}
          context={context}
        />
      )}
      <button
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          border: "2px solid currentColor",
          borderRadius: "6px",
          backgroundColor: "inherit",
          cursor: "pointer",
          width: "100%",
          height: "30px",
          fontWeight: "500",
        }}
        onClick={() => setIsPreviewOpen(!isPreviewOpen)}
      >
        {isPreviewOpen ? "Hide" : "Show"} Bundle Preview ({totalQuantity}){" "}
        <ChevronDown
          size={24}
          style={{
            transition: "transform 0.4s ease",
            transform: `rotate(${isPreviewOpen ? 180 : 0}deg)`,
          }}
        />
      </button>

      <div
        style={{
          width: "100%",
          maxHeight: isPreviewOpen ? "300px" : "0",
          overflow: "hidden",
          transition: "max-height 0.3s ease",
        }}
      >
        <BundlePreview />
      </div>
    </div>
  );
};
